// Slice 2 client: scans, identity, guide, queue, moderation, AI settings.
// Shapes mirror src/teacher/{scans,identity,approval,guide,moderation}.ts.

import { api, ApiErr, me } from './teacher-api';

/** Multipart write (file uploads). Same CSRF header as api(); the browser
 *  sets the multipart boundary, so no Content-Type here. */
export async function upload<T>(method: string, path: string, form: FormData): Promise<T> {
  const res = await fetch(path, { method, credentials: 'same-origin', headers: { 'X-Requested-With': 'tos' }, body: form });
  const json = await res.json().catch(() => ({}));
  if (res.status === 401) { me.value = null; location.assign(`/login?next=${encodeURIComponent(location.pathname + location.search)}`); }
  if (!res.ok) throw new ApiErr(res.status, json.error ?? `HTTP ${res.status}`, json.fields, json.current);
  return json as T;
}

export const errText = (e: unknown): string => {
  const x = e as ApiErr;
  if (!x || typeof x !== 'object') return String(e);
  const fields = x.fields ? Object.values(x.fields).filter((v) => v && v !== x.error) : [];
  return fields.length ? `${x.error}: ${fields.join(' · ')}` : (x.error ?? x.message);
};

// ── Scans ─────────────────────────────────────────────────────────────

export type Attendance = 'present' | 'absent' | 'catchup' | 'exempt';
export interface TrackerRow {
  student_id: string; name: string; student_code: string | null; attendance: Attendance; identity_status: 'unverified' | 'verified'; revision: number;
  paper_id: string | null; scan_number: string | null; batch_id: string | null; mapping_status: MappingStatus | null;
}
export type MappingStatus = 'unresolved' | 'proposed' | 'confirmed';
export interface Reconciliation {
  status: 'reconciled' | 'blocked';
  blockers: { key: string; kind: string; detail: string }[];
  discrepancies: { key: string; kind: 'paper_missing' | 'unresolved_paper'; detail: string; accepted: boolean; note?: string }[];
}
export interface Batch {
  id: string; class_id: string; kind: 'main' | 'catchup'; pages_per_paper: number; file_sha256: string; file_size: number; page_count: number;
  status: 'uploaded' | 'reconciled' | 'blocked'; excluded_pages: { page: number; note: string }[]; accepted: { key: string; note: string }[]; revision: number;
  uploaded_at: string; reconciliation: Reconciliation;
}
export type IdentityStatus = 'verified' | 'proceed_number_only' | 'proceed_name_only' | 'check_required' | 'teacher_review';
export interface ScanPaper {
  id: string; batch_id: string | null; class_id: string; scan_number: string; first_page: number | null; last_page: number | null; student_id: string | null;
  student_name: string | null; mapping_status: MappingStatus; mapping_revision: number; ai_identity_status: IdentityStatus | null;
  ai_identity_evidence: string | null; quarantined: number; quarantine_reason: string | null;
}
export interface ScansView {
  assessment_revision: number; pages_per_paper: number | null;
  tracker: { class_id: string; name: string; rows: TrackerRow[] }[];
  batches: Batch[]; papers: ScanPaper[];
}
export interface IdentityEvidence { read?: { name_text: string | null; number_text: string | null; code_text: string | null }; student_id?: string | null; detail?: string }

export function parseEvidence(raw: string | IdentityEvidence | null): IdentityEvidence | null {
  if (!raw) return null;
  if (typeof raw !== 'string') return raw;
  try { return JSON.parse(raw) as IdentityEvidence; } catch { return null; }
}

// SOP §21 labels (en dashes replaced with a colon).
export const IDENTITY_LABEL: Record<IdentityStatus, { label: string; bg: string; hint: string }> = {
  verified: { label: 'IDENTITY VERIFIED', bg: '#d3f0d9', hint: 'All relevant evidence agrees.' },
  proceed_number_only: { label: 'NAME UNCLEAR: ID MATCHES TRACKER', bg: '#fde9b8', hint: 'Proceed if the scan number is reliable. Confirm this paper.' },
  proceed_name_only: { label: 'SCAN NUMBER/ID UNCLEAR: NAME MATCHES TRACKER', bg: '#fde9b8', hint: 'Proceed cautiously. Confirm this paper.' },
  check_required: { label: 'IDENTITY CHECK REQUIRED', bg: '#f8cdcd', hint: 'Evidence conflicts. Pick the student.' },
  teacher_review: { label: 'TEACHER REVIEW: STUDENT IDENTITY UNCERTAIN', bg: '#f8cdcd', hint: 'Nothing usable was read. Pick the student.' },
};

/** Processing status of a scanned paper (SOP §27 wording). */
export function paperPill(p: Pick<ScanPaper, 'mapping_status' | 'quarantined' | 'batch_id'>): { label: string; bg: string } {
  if (!p.batch_id) return { label: 'NOT SCANNED', bg: '#e2e3e6' };
  if (p.quarantined) return { label: 'QUARANTINED', bg: '#f8cdcd' };
  if (p.mapping_status === 'confirmed') return { label: 'IDENTITY VERIFIED', bg: '#d3f0d9' };
  if (p.mapping_status === 'proposed') return { label: 'SCANNED', bg: '#dbe7fb' };
  return { label: 'UNRESOLVED', bg: '#fddcbf' };
}

// ── Jobs ──────────────────────────────────────────────────────────────

export interface Job { id: string; kind: string; status: 'queued' | 'running' | 'done' | 'failed'; error: string | null; log: string[]; paper_ids: string[] }
export interface Estimate { papers: number; calls: number; items: number; pages: number; approx_input_tokens: number }

/** Polls a job until it finishes. */
export async function waitForJob(id: string, onTick?: (j: Job) => void): Promise<Job> {
  for (;;) {
    const j = await api<Job>('GET', `/api/t/jobs/${id}`);
    onTick?.(j);
    if (j.status === 'done' || j.status === 'failed') return j;
    await new Promise((r) => setTimeout(r, 800));
  }
}

// ── Queue ─────────────────────────────────────────────────────────────

export type Confidence = 'high' | 'medium' | 'low';
export interface Proposal {
  id: string; source: string; status: 'pending' | 'approved'; mark: number | null; unavailable_reason: string | null; blank: boolean; evidence: string | null;
  codes: string[]; confidence: Confidence | null; review_reason: string | null; flags: string[]; stale: string | null; teacher_changed: boolean;
  spot_check: boolean; spot_checked: boolean; decision_id: string | null;
}
export interface QueueCell { item_id: string; proposal: Proposal | null; result: { mark: number | null; unavailable_reason: string | null; blank: boolean; revision: number; proposal_id: string | null } | null }
export type PaperStatus = 'SCANNED' | 'IDENTITY_VERIFIED' | 'READY' | 'REVIEW_REQUIRED' | 'APPROVED';
export interface QueuePaper {
  paper_id: string; scan_number: string; class_id: string; class_name: string; batch_id: string; first_page: number | null; last_page: number | null;
  mapping_status: MappingStatus; mapping_revision: number; quarantined: boolean; ai_identity_status: IdentityStatus | null; ai_identity_evidence: IdentityEvidence | null;
  student_id: string | null; student_name: string | null; enrolment: { revision: number; attendance: Attendance; identity_status: string } | null;
  status: PaperStatus; ai_marked: boolean; proposed_total: number; max_total: number; full_marks: boolean; lowest_confidence: Confidence | null;
  needs_review: number; viewed: boolean; cells: QueueCell[];
}
export interface Queue { assessment: { id: string; current_guide_version: number; moderation_version: number; map_revision: number }; items: { id: string; label: string; max_mark: number }[]; papers: QueuePaper[] }

export const PAPER_STATUS: Record<PaperStatus, { label: string; bg: string }> = {
  SCANNED: { label: 'SCANNED', bg: '#dbe7fb' },
  IDENTITY_VERIFIED: { label: 'IDENTITY VERIFIED', bg: '#e2e3e6' },
  READY: { label: 'READY FOR APPROVAL', bg: '#d3f0d9' },
  REVIEW_REQUIRED: { label: 'REVIEW REQUIRED', bg: '#fddcbf' },
  APPROVED: { label: 'TEACHER APPROVED', bg: '#b9e6c4' },
};
export const CONF_STYLE: Record<Confidence, { label: string; bg: string }> = {
  high: { label: 'High confidence', bg: '#d3f0d9' },
  medium: { label: 'Medium confidence', bg: '#fde9b8' },
  low: { label: 'Low confidence: check', bg: '#f8cdcd' },
};

export function markText(x: { mark: number | null; unavailable_reason: string | null; blank: boolean } | null): string {
  if (!x) return '-';
  if (x.blank) return 'blank';
  if (x.mark !== null) return String(x.mark);
  return x.unavailable_reason ?? 'no mark';
}

/** Why a pending proposal cannot go in "Approve all" (null = it can). */
export function bulkBlock(p: QueuePaper, pr: Proposal): string | null {
  if (pr.status !== 'pending') return 'not pending';
  if (!p.student_id || !p.enrolment || p.enrolment.identity_status !== 'verified') return 'identity not verified';
  if (p.enrolment.attendance !== 'present') return `attendance is ${p.enrolment.attendance}`;
  if (pr.stale) return `stale: ${pr.stale}`;
  if (pr.teacher_changed) return 'teacher changed this cell';
  if (pr.confidence === 'low') return 'low confidence: decide this one yourself';
  if (pr.mark === null && pr.unavailable_reason === null && !pr.blank) return 'no usable mark';
  return null;
}

/** Why a whole paper is held back from "Approve all" (S22). */
export function paperBlock(p: QueuePaper): string | null {
  if (p.full_marks && !p.viewed) return 'full marks: open it first';
  const unticked = p.cells.filter((c) => c.proposal?.status === 'pending' && c.proposal.spot_check && !c.proposal.spot_checked).length;
  if (unticked) return `${unticked} spot check${unticked === 1 ? '' : 's'} to tick`;
  return null;
}

// ── Guide / moderation ────────────────────────────────────────────────

export interface GuideItem {
  item_id: string; item_label: string; expected_response: string; alternatives: string; partial_credit_rules: string; required_working: string;
  wrong_interpretations: { text: string; code_ids: string[] }[];
}
export interface Guide {
  current_version: number; version: number; school_guide_text: string | null; covers_all?: boolean; items: GuideItem[];
  versions: { version: number; created_by: string; created_at: string }[]; marking_started: boolean;
}
export interface DecisionBody {
  item_id: string | null; original_interpretation: string; teacher_decision: string; scope: 'student' | 'class' | 'assessment';
  scope_student_id?: string; scope_class_id?: string; applies_from_paper_id?: string; retro: boolean; notes?: string; decided_on?: string;
}
export interface Recheck {
  id: string; status: 'pending' | 'changed' | 'unchanged'; student_id: string; student_name: string; item_id: string; item_label: string; result_id: string;
  mark: number | null; paper_id: string | null; note: string | null;
}
export interface Decision {
  id: string; code: string; number: number; version: number; decided_on: string; item_id: string | null; original_interpretation: string; teacher_decision: string;
  applies_from_paper_id: string | null; scope: 'student' | 'class' | 'assessment'; scope_student_id: string | null; scope_class_id: string | null; retro: boolean;
  recheck_status: 'not_required' | 'pending' | 'complete'; notes: string; created_at: string; rechecks: Recheck[];
}

// ── AI settings ───────────────────────────────────────────────────────

export interface AiSettings {
  provider: string | null; model: string | null;
  /** false until TEACHER_SECRET is set on the server. */
  canSaveKeys: boolean; useServerKeys: boolean;
  providers: {
    id: string; enabled: boolean; hasKey: boolean; unsuitable: boolean; defaultModel: string;
    keySource: 'yours' | 'server' | null; last4: string | null; needsReentry: boolean;
  }[];
}
